import {
  advancedSelectionOptions,
  markFarmAsFavourite,
  mergeFarmsWithKpis,
  VECTOR_KPIS,
} from '@/components/FarmsTable/utils';
import { selectColumns } from '@/reducers/tableColumnsSlice';
import { useAppSelector } from '@/reducers/rootReducer';
import { userDetailsSelectors } from '@/reducers/userDetailsSlice';
import { useGetFarmsKpisQuery, useGetFarmsQuery } from '@/rtk/farmsApi';
import { useGetFavouriteFarmIdsQuery } from '@/rtk/userPreferencesApi';
import { useMemo } from 'react';

const useFarms = () => {
  const dynamicColumns = useAppSelector(selectColumns);
  const preferredSystems = useAppSelector(
    userDetailsSelectors.selectPreferredSystems,
  );

  const { data: allFarms = [], ...farmsRequestState } = useGetFarmsQuery(
    preferredSystems,
    {
      skip: !preferredSystems?.length,
    },
  );
  const { data: favouriteIds = [], ...favouriteRequestState } =
    useGetFavouriteFarmIdsQuery(undefined, {
      skip: allFarms.length === 0,
    });

  const shouldFetchKpis = useMemo(() => {
    return (
      allFarms.length > 0 &&
      [...VECTOR_KPIS, ...advancedSelectionOptions].some((kpi) =>
        dynamicColumns?.some((col) => col.key === kpi.key),
      )
    );
  }, [allFarms, dynamicColumns]);

  const { data: kpis, ...kpisRequestState } = useGetFarmsKpisQuery(undefined, {
    skip: !shouldFetchKpis,
  });

  const farms = useMemo(() => {
    let res = allFarms.map((farm) =>
      markFarmAsFavourite(favouriteIds || [], farm),
    );

    if (shouldFetchKpis && kpis) {
      res = mergeFarmsWithKpis(res, kpis);
    }

    return res;
  }, [allFarms, favouriteIds, shouldFetchKpis, kpis]);

  const favouriteFarms = useMemo(
    () => farms.filter((farm) => farm.isFavourite),
    [farms],
  );

  return {
    farms,
    favouriteFarms,
    isFarmsLoading: farmsRequestState.isFetching || kpisRequestState.isFetching,
    isError: farmsRequestState.isError || favouriteRequestState.isError,
  };
};

export default useFarms;
